import { Link, useLocation } from "wouter";
import { useClerk } from "@clerk/react";
import { 
  Heart, 
  Info, 
  Pill, 
  Link as LinkIcon, 
  MessageCircle,
  LogOut,
  Menu,
  BookOpen,
  Stethoscope,
  Settings,
  Activity,
  FileText,
  ShieldAlert,
  Eye,
  UserPlus,
  LogIn,
  Lock,
  Dumbbell,
} from "lucide-react";
import { LastSignInBanner } from "@/components/ui/last-signin-banner";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { SessionNoticeBanner } from "@/components/ui/session-notice-banner";
import { LegalFooter } from "@/components/layout/legal-footer";
import { GlobalSearch } from "@/components/ui/global-search";
import { clearRheumHubLocalStorage } from "@/lib/local-storage-keys";

function GuestBanner() {
  const [, setLocation] = useLocation();

  const handleSignUp = () => {
    localStorage.removeItem("rheumhub_role");
    setLocation("/sign-up");
  };

  const handleSignIn = () => {
    localStorage.removeItem("rheumhub_role");
    setLocation("/sign-in");
  };

  return (
    <div className="bg-amber-50 border-b border-amber-200">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-2.5 flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <Eye className="h-4 w-4 text-amber-600 shrink-0" />
          <p className="text-sm text-amber-800">
            <span className="font-semibold">Browsing as guest.</span>{" "}
            <span className="hidden sm:inline">Create a free account to unlock the AI guide, symptom checker, and personalised features.</span>
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <button
            onClick={handleSignIn}
            className="inline-flex items-center gap-1.5 text-xs font-medium text-amber-700 hover:text-amber-900 hover:underline focus:outline-none focus:underline"
          >
            <LogIn className="h-3.5 w-3.5" />
            Sign in
          </button>
          <button
            onClick={handleSignUp}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold bg-amber-600 hover:bg-amber-700 text-white rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-amber-500 focus:ring-offset-1"
          >
            <UserPlus className="h-3.5 w-3.5" />
            Create account
          </button>
        </div>
      </div>
    </div>
  );
}

export function PatientLayout({ children, title }: { children: React.ReactNode, title: string }) {
  const [location, setLocation] = useLocation();
  const { signOut } = useClerk();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const isGuest = localStorage.getItem("rheumhub_role") === "guest";

  // Two-row navigation. Row 1 is the day-to-day clinical interaction
  // surface (always quickest to reach). Row 2 is the learn & support
  // library (reference content the patient browses, not actions). Settings
  // is intentionally not in the nav list — it's reachable via the gear
  // icon on the right of row 1.
  const primaryNav = [
    { href: "/patient", label: "Home", icon: Heart, guestAllowed: true },
    { href: "/patient/triage", label: "Symptom Check", icon: Activity, guestAllowed: false },
    { href: "/patient/requests", label: "Advice Line", icon: FileText, guestAllowed: false },
    { href: "/patient/chat", label: "AI Guide", icon: MessageCircle, guestAllowed: false },
  ];

  const secondaryNav = [
    { href: "/patient/conditions", label: "Conditions", icon: Info, guestAllowed: true },
    { href: "/patient/exercises", label: "Exercises", icon: Dumbbell, guestAllowed: true },
    { href: "/patient/resources", label: "Resources", icon: LinkIcon, guestAllowed: true },
    { href: "/patient/safeguarding", label: "Crisis Support", icon: ShieldAlert, guestAllowed: true },
  ];

  const visiblePrimary = isGuest ? primaryNav.filter((i) => i.guestAllowed) : primaryNav;
  const visibleSecondary = isGuest ? secondaryNav.filter((i) => i.guestAllowed) : secondaryNav;
  const lockedPrimary = isGuest ? primaryNav.filter((i) => !i.guestAllowed) : [];
  const lockedSecondary = isGuest ? secondaryNav.filter((i) => !i.guestAllowed) : [];

  const isItemActive = (href: string) =>
    location === href || (location.startsWith(href) && href !== "/patient");

  const handleSignOut = () => {
    clearRheumHubLocalStorage();
    signOut({ redirectUrl: "/" });
  };

  const handleGuestSignUp = () => {
    localStorage.removeItem("rheumhub_role");
    setLocation("/sign-up");
  };

  const handleGuestSignIn = () => {
    localStorage.removeItem("rheumhub_role");
    setLocation("/sign-in");
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7] flex flex-col font-sans">
      <SessionNoticeBanner />
      <LastSignInBanner />
      {/* Guest banner — shown persistently when browsing without an account */}
      {isGuest && <GuestBanner />}
      {/* Top Navigation — brand + primary nav + account controls in one
          sticky row. The secondary "Learn & Support" strip lives inside
          <main> directly above the page title (see below) rather than
          in this header, per user preference. */}
      <header className="bg-white border-b border-rose-100/50 sticky top-0 z-20 shadow-sm">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-2">
              <Heart className="h-6 w-6 text-rose-500 fill-rose-50" />
              <span className="text-xl font-bold text-slate-800 tracking-tight">RheumHub</span>
            </div>

            {/* Primary nav lives below the header in <main> (above the
                Learn & Support row + page title). Header keeps only the
                brand, account controls, and mobile hamburger. */}

            {/* Account controls (desktop only) — search / settings /
                sign-out, or guest sign-in / create-account CTAs. On
                mobile these live in the hamburger menu instead. */}
            <div className="hidden md:flex items-center gap-2 shrink-0 ml-auto">
              {!isGuest && <GlobalSearch />}
              {isGuest ? (
                <>
                  <button
                    onClick={handleGuestSignIn}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-lg transition-colors"
                  >
                    <LogIn className="h-4 w-4" />
                    Sign in
                  </button>
                  <button
                    onClick={handleGuestSignUp}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-semibold bg-rose-500 hover:bg-rose-600 text-white rounded-lg transition-colors"
                  >
                    <UserPlus className="h-4 w-4" />
                    Create account
                  </button>
                </>
              ) : (
                <>
                  <Link href="/patient/settings">
                    <Button variant="ghost" size="sm" className="text-slate-500 hover:text-slate-900" data-testid="nav-settings" aria-label="Settings">
                      <Settings className="h-4 w-4" />
                    </Button>
                  </Link>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleSignOut}
                    className="text-slate-500 hover:text-slate-900 hover:bg-slate-100"
                    data-testid="nav-signout"
                  >
                    <LogOut className="h-4 w-4 mr-1.5" />
                    Sign out
                  </Button>
                </>
              )}
            </div>

            {/* Mobile menu button */}
            <div className="md:hidden flex items-center">
              <Button variant="ghost" size="icon" onClick={() => setMobileMenuOpen(!mobileMenuOpen)} aria-label="Open menu">
                <Menu className="h-6 w-6 text-slate-600" />
              </Button>
            </div>
          </div>

        </div>

        {/* Mobile Nav */}
        {mobileMenuOpen && (
          <div className="md:hidden border-t border-slate-100 bg-white">
            <div className="pt-2 pb-3 space-y-1">
              {/* Primary section */}
              <div className="px-4 pt-1 pb-1 text-[11px] uppercase tracking-widest font-semibold text-slate-400">
                Day to day
              </div>
              {visiblePrimary.map((item) => {
                const isActive = isItemActive(item.href);
                const Icon = item.icon;
                return (
                  <Link
                    key={item.href}
                    href={item.href}
                    onClick={() => setMobileMenuOpen(false)}
                    className={`block pl-3 pr-4 py-2 border-l-4 text-base font-medium ${
                      isActive
                        ? "bg-rose-50 border-rose-500 text-rose-700"
                        : "border-transparent text-slate-600 hover:bg-slate-50 hover:border-slate-300 hover:text-slate-800"
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <Icon className="h-5 w-5 opacity-70" />
                      {item.label}
                    </div>
                  </Link>
                );
              })}
              {lockedPrimary.map((item) => (
                <button
                  key={item.href}
                  onClick={() => { setMobileMenuOpen(false); handleGuestSignUp(); }}
                  className="block w-full text-left pl-3 pr-4 py-2 border-l-4 border-transparent text-base font-medium text-slate-300"
                >
                  <div className="flex items-center gap-3">
                    <Lock className="h-5 w-5" />
                    {item.label}
                    <span className="text-xs text-slate-300 ml-auto mr-4">Sign up to unlock</span>
                  </div>
                </button>
              ))}

              {/* Secondary section */}
              <div className="px-4 pt-3 pb-1 text-[11px] uppercase tracking-widest font-semibold text-slate-400">
                Learn &amp; support
              </div>
              {visibleSecondary.map((item) => {
                const isActive = isItemActive(item.href);
                const Icon = item.icon;
                return (
                  <Link
                    key={item.href}
                    href={item.href}
                    onClick={() => setMobileMenuOpen(false)}
                    className={`block pl-3 pr-4 py-2 border-l-4 text-base font-medium ${
                      isActive
                        ? "bg-rose-50 border-rose-500 text-rose-700"
                        : "border-transparent text-slate-600 hover:bg-slate-50 hover:border-slate-300 hover:text-slate-800"
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <Icon className="h-5 w-5 opacity-70" />
                      {item.label}
                    </div>
                  </Link>
                );
              })}
              {lockedSecondary.map((item) => (
                <button
                  key={item.href}
                  onClick={() => { setMobileMenuOpen(false); handleGuestSignUp(); }}
                  className="block w-full text-left pl-3 pr-4 py-2 border-l-4 border-transparent text-base font-medium text-slate-300"
                >
                  <div className="flex items-center gap-3">
                    <Lock className="h-5 w-5" />
                    {item.label}
                    <span className="text-xs text-slate-300 ml-auto mr-4">Sign up to unlock</span>
                  </div>
                </button>
              ))}
              {isGuest ? (
                <>
                  <button
                    onClick={() => { setMobileMenuOpen(false); handleGuestSignIn(); }}
                    className="block w-full text-left pl-3 pr-4 py-2 border-l-4 border-transparent text-base font-medium text-slate-600 hover:bg-slate-50 hover:border-slate-300 hover:text-slate-800"
                  >
                    <div className="flex items-center gap-3">
                      <LogIn className="h-5 w-5 opacity-70" />
                      Sign in
                    </div>
                  </button>
                  <button
                    onClick={() => { setMobileMenuOpen(false); handleGuestSignUp(); }}
                    className="block w-full text-left pl-3 pr-4 py-2 border-l-4 border-rose-400 bg-rose-50 text-base font-semibold text-rose-700"
                  >
                    <div className="flex items-center gap-3">
                      <UserPlus className="h-5 w-5 opacity-70" />
                      Create account
                    </div>
                  </button>
                </>
              ) : (
                <>
                  <Link
                    href="/patient/settings"
                    onClick={() => setMobileMenuOpen(false)}
                    className="block pl-3 pr-4 py-2 border-l-4 border-transparent text-base font-medium text-slate-600 hover:bg-slate-50 hover:border-slate-300 hover:text-slate-800"
                  >
                    <div className="flex items-center gap-3">
                      <Settings className="h-5 w-5 opacity-70" />
                      Privacy & Settings
                    </div>
                  </Link>
                  <button
                    onClick={() => { setMobileMenuOpen(false); handleSignOut(); }}
                    className="block w-full text-left pl-3 pr-4 py-2 border-l-4 border-transparent text-base font-medium text-slate-600 hover:bg-slate-50 hover:border-slate-300 hover:text-slate-800"
                  >
                    <div className="flex items-center gap-3">
                      <LogOut className="h-5 w-5 opacity-70" />
                      Sign out
                    </div>
                  </button>
                </>
              )}
            </div>
          </div>
        )}
      </header>

      {/* Main Content */}
      <main className="flex-1 max-w-5xl w-full mx-auto px-4 sm:px-6 lg:px-8 py-6 md:py-8">
        {/* Primary nav (Home / Symptom Check / Advice Line / AI Guide)
            — desktop only; sits above the Learn & Support strip so the
            day-to-day actions are the first thing under the brand bar.
            Mobile keeps the same items inside the hamburger menu. */}
        {(visiblePrimary.length > 0 || lockedPrimary.length > 0) && (
          <nav
            className="hidden md:flex items-center gap-1 mb-3 -mx-4 sm:-mx-6 lg:-mx-8 px-4 sm:px-6 lg:px-8 py-2"
            aria-label="Primary"
          >
            {visiblePrimary.map((item) => {
              const isActive = isItemActive(item.href);
              const Icon = item.icon;
              return (
                <Link
                  key={item.href}
                  href={item.href}
                  className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-semibold transition-colors ${
                    isActive
                      ? "bg-rose-50 text-rose-700"
                      : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"
                  }`}
                  data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  <Icon className="h-4 w-4" />
                  {item.label}
                </Link>
              );
            })}
            {lockedPrimary.map((item) => (
              <button
                key={item.href}
                onClick={handleGuestSignUp}
                title="Create a free account to unlock this"
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium text-slate-300 hover:text-slate-400 hover:bg-slate-50 transition-colors"
              >
                <Lock className="h-3.5 w-3.5" />
                {item.label}
              </button>
            ))}
          </nav>
        )}
        {/* Learn & Support strip — sits directly above the page title on
            every viewport, scrolls horizontally on narrow widths so all
            six links remain reachable without a menu. */}
        {(visibleSecondary.length > 0 || lockedSecondary.length > 0) && (
          <nav
            className="flex items-center gap-1 mb-6 -mx-4 sm:-mx-6 lg:-mx-8 px-4 sm:px-6 lg:px-8 py-2 border-y border-rose-100/60 bg-rose-50/30 overflow-x-auto"
            aria-label="Learn and support"
          >
            <span className="text-[11px] uppercase tracking-widest font-semibold text-slate-500 mr-3 shrink-0">
              Learn &amp; Support
            </span>
            {visibleSecondary.map((item) => {
              const isActive = isItemActive(item.href);
              const Icon = item.icon;
              return (
                <Link
                  key={item.href}
                  href={item.href}
                  className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium transition-colors whitespace-nowrap ${
                    isActive
                      ? "bg-rose-100 text-rose-700"
                      : "text-slate-600 hover:text-slate-900 hover:bg-white"
                  }`}
                  data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  <Icon className="h-3.5 w-3.5 opacity-70" />
                  {item.label}
                </Link>
              );
            })}
            {lockedSecondary.map((item) => (
              <button
                key={item.href}
                onClick={handleGuestSignUp}
                title="Create a free account to unlock this"
                className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium text-slate-400 hover:text-slate-600 hover:bg-white transition-colors whitespace-nowrap"
              >
                <Lock className="h-3 w-3" />
                {item.label}
              </button>
            ))}
          </nav>
        )}
        {title && (
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-slate-900 tracking-tight">{title}</h1>
          </div>
        )}
        {children}
      </main>
      
      {/* Footer */}
      <div className="mt-auto">
        <div className="bg-white border-t border-rose-100/50 py-3 px-4 sm:px-6 lg:px-8">
          <p className="text-xs text-slate-400 text-center">
            For medical emergencies, always call <strong>999</strong>. For urgent medical advice, call <strong>NHS 111</strong>. AI responses do not constitute medical advice.
          </p>
        </div>
        <LegalFooter
          copyright="© RheumHub · For UK patients and healthcare professionals"
          extraLinks={[
            { label: "Your Data", href: "/patient/settings" },
            { label: "Crisis Support", href: "/patient/safeguarding" },
          ]}
        />
      </div>
    </div>
  );
}
